import { Alert, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { UserRank } from "@types_def/models/user.type";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import MemberForm from "@/components/organization/Member/Form";
import { useAddMemberToOrganizationMutation } from "@/libs/generated/hooks";
import type { MemberFormSchemaType } from "@/schemas/organization-form/member.schema";
import { MemberFormSchemaDefaultValues } from "@/schemas/organization-form/member.schema";

type AddMemberDialogProps = {
  open: boolean;
  onClose: () => void;
  organizationSlug: string;
  onSuccess?: () => void;
};

export default function AddMemberDialog({
  open,
  onClose,
  organizationSlug,
  onSuccess,
}: AddMemberDialogProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isManagersPage = pathname.includes("/view-managers");
  const queryClient = useQueryClient();

  const {
    isPending,
    error,
    mutateAsync: addMemberToOrganization,
  } = useAddMemberToOrganizationMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
      toast.success(t("network.organization.member.created"), {
        position: "bottom-right",
      });
      onSuccess?.();
      onClose();
    },
  });

  const handleSubmit = async (data: MemberFormSchemaType) => {
    try {
      await addMemberToOrganization({
        AddMemberToOrganizationInput: {
          organizationId: organizationSlug,
          assigneeFirstName: data.assigneeFirstName,
          assigneeLastName: data.assigneeLastName,
          assigneeEmail: data.assigneeEmail,
          assigneePhone: data.assigneePhone,
          assigneeFunction: data.assigneeFunction,
          assigneeService: data.assigneeService,
          assigneeRank: isManagersPage
            ? UserRank.ORGANIZATION_MANAGER
            : UserRank.ORGANIZATION_MEMBER,
        },
      });
    } catch (_err) {}
  };

  const handleClose = () => {
    if (!isPending) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isPending}
    >
      <DialogTitle>{t("organization.actions.add-member.title")}</DialogTitle>
      <DialogContent>
        {error != null && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error instanceof Error ? error.message : t("common.error.generic")}
          </Alert>
        )}
        <MemberForm
          defaultValues={MemberFormSchemaDefaultValues}
          onSubmit={handleSubmit}
          isLoading={isPending}
        />
      </DialogContent>
    </Dialog>
  );
}
